
import { RelatedQuestionItem } from './types';

/**
 * Type guard to check if a question item has been generated (has research_id)
 * Handles both object format and stringified JSON format
 */
export const isGeneratedQuestion = (item: RelatedQuestionItem): item is { question: string; research_id: string } => {
  // Handle object format: {question: "...", research_id: "..."}
  if (typeof item === 'object' && item !== null && 'research_id' in item) {
    return true;
  }
  
  // Handle stringified JSON format: '{"question":"...","research_id":"..."}'
  if (typeof item === 'string') {
    try {
      const parsed = JSON.parse(item);
      return typeof parsed === 'object' && parsed !== null && 'research_id' in parsed;
    } catch {
      // Not valid JSON, treat as regular string question
      return false;
    }
  }
  
  return false;
};

/**
 * Extract the display text from any question item type
 * Handles both object format and stringified JSON format
 */
export const getQuestionText = (item: RelatedQuestionItem): string => {
  if (typeof item === 'string') {
    // Try to parse as JSON first (for stringified generated questions)
    try {
      const parsed = JSON.parse(item);
      if (typeof parsed === 'object' && parsed !== null && 'question' in parsed) {
        return parsed.question;
      }
    } catch {
      // Not valid JSON, treat as regular string question
    }
    // Return the string as-is for regular string questions
    return item;
  }
  return item.question;
};

/**
 * Extract the research ID from a generated question item
 * Handles both object format and stringified JSON format
 */
export const getResearchId = (item: RelatedQuestionItem): string | null => {
  // Handle object format
  if (typeof item === 'object' && item !== null && 'research_id' in item) {
    return item.research_id;
  }
  
  // Handle stringified JSON format
  if (typeof item === 'string') {
    try {
      const parsed = JSON.parse(item);
      if (typeof parsed === 'object' && parsed !== null && 'research_id' in parsed) {
        return parsed.research_id;
      }
    } catch {
      // Not valid JSON, no research ID
    }
  }
  
  return null;
};

/**
 * Validate that a question text is not empty
 */
export const isValidQuestionText = (text: string): boolean => {
  return text.trim().length > 0;
};

/**
 * Extract clean question text for display (final processing layer)
 * Specifically handles Generated Research components that may have JSON strings
 */
export const extractCleanQuestionText = (text: string): string => {
  // If it looks like a JSON string, try to extract the question field
  if (text.trim().startsWith('{') && text.includes('"question"')) {
    try {
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed === 'object' && 'question' in parsed) {
        return parsed.question;
      }
    } catch {
      // If parsing fails, fall through to return original text
    }
  }
  
  // Return original text for normal questions or if parsing failed
  return text;
};

/**
 * Truncate question text for display
 */
export const truncateQuestion = (text: string, maxLength: number = 100): string => {
  if (text.length <= maxLength) return text;
  return text.substring(0, maxLength) + '...';
};

/**
 * Transform a string question to a generated question object
 */
export const createGeneratedQuestion = (questionText: string, researchId: string): { question: string; research_id: string } => {
  return {
    question: questionText,
    research_id: researchId
  };
};
